// The "…" menu of lifecycle actions for one event.
//
// Extracted from EventCard so the list view can offer the same actions from a
// table row. Which items appear is decided here from the event's status; the
// caller only supplies handlers for what the viewer is allowed to do.
//
// Rendered into a portal: `.card` sets overflow-hidden for its rounded corners,
// which clipped the menu to the card's edge — the items rendered, were correct,
// and could not be read. That made "Buka kembali" effectively missing from the
// only screen a closed event appears on.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Archive, ArchiveRestore, Ban, MoreVertical, Pencil, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/format';
import type { AppEvent } from '@/types/domain';

export interface EventActionMenuProps {
  event: AppEvent;
  onEdit?: (event: AppEvent) => void;
  onArchive?: (event: AppEvent) => void;
  onUnarchive?: (event: AppEvent) => void;
  onReopen?: (event: AppEvent) => void;
  onVoid?: (event: AppEvent) => void;
}

const WIDTH = 210;

export function EventActionMenu({
  event,
  onEdit,
  onArchive,
  onUnarchive,
  onReopen,
  onVoid,
}: EventActionMenuProps) {
  const isClosed = event.status === 'closed';
  const isOpen = event.status === 'active';
  // Only an event nothing has been sold in can be voided. The database refuses
  // the rest anyway; hiding the option keeps people from meeting that error.
  const canVoid = event.totalTransactions === 0 && event.status !== 'void';

  const items: { label: string; icon: React.ReactNode; run: () => void; danger?: boolean }[] = [];
  if (isOpen && onEdit) items.push({ label: 'Edit detail', icon: <Pencil size={14} />, run: () => onEdit(event) });
  if (isClosed && onReopen) items.push({ label: 'Buka kembali', icon: <RotateCcw size={14} />, run: () => onReopen(event) });
  if (event.status === 'archived' && onUnarchive)
    items.push({ label: 'Kembalikan dari arsip', icon: <ArchiveRestore size={14} />, run: () => onUnarchive(event) });
  if ((isOpen || isClosed) && onArchive)
    items.push({ label: 'Arsipkan', icon: <Archive size={14} />, run: () => onArchive(event) });
  if (canVoid && onVoid)
    items.push({ label: 'Void event', icon: <Ban size={14} />, run: () => onVoid(event), danger: true });

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      setPos({
        top: r.top + window.scrollY,
        left: Math.max(8, r.right + window.scrollX - WIDTH),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // `click`, not `mousedown`: mousedown fires first, and unmounting the
    // panel there makes the browser dispatch the click on the common ancestor
    // of the mousedown and mouseup targets instead of the control under the
    // cursor — so the first click outside is silently eaten.
    document.addEventListener('click', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={triggerRef} className="inline-flex">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Aksi lain"
        title="Aksi lain"
      >
        <MoreVertical size={16} className="text-ink-subtle" />
      </Button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ top: pos.top, left: pos.left, width: WIDTH }}
          className="absolute z-50 -translate-y-full -mt-1 rounded border border-line bg-surface shadow-lg py-1"
        >
          {items.map((item) => (
            <button
              key={item.label}
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                item.run();
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-small text-left',
                'hover:bg-canvas focus:bg-canvas focus:outline-none',
                item.danger ? 'text-danger' : 'text-ink',
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
